import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import API, { resolveMediaUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  PaperPlaneTilt, Paperclip, X, DownloadSimple, Play, Image,
  File as FileIcon, MusicNote, CaretLeft, ChatCircleDots,
  ChatTeardropText, MagnifyingGlass
} from '@phosphor-icons/react';

const BEEP_FREQ = 800;
const BEEP_DUR = 150;

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = BEEP_FREQ;
    osc.type = 'sine';
    gain.gain.value = 0.3;
    osc.start();
    osc.stop(ctx.currentTime + BEEP_DUR / 1000);
  } catch (e) { /* silent */ }
}

function FilePreview({ file, onRemove }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (file && file.type?.startsWith('image')) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);
  if (!file) return null;
  return (
    <div className="flex items-center gap-2 p-2 mx-3 mb-2 border border-border rounded-lg bg-muted/30" data-testid="file-preview">
      {preview ? (
        <img src={preview} alt="" className="w-12 h-12 rounded object-cover" />
      ) : (
        <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
          {file.type?.startsWith('video') ? <Play size={20} /> : file.type?.startsWith('audio') ? <MusicNote size={20} /> : <FileIcon size={20} />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{file.name}</p>
        <p className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
      </div>
      <button onClick={onRemove} className="p-1 hover:bg-accent rounded"><X size={14} /></button>
    </div>
  );
}

function MediaBubble({ msg }) {
  const [loadError, setLoadError] = useState(false);
  const ft = (msg.file_type || msg.media_type || '').toLowerCase();
  const rawUrl = msg.file_url || msg.media_url || '';
  const url = resolveMediaUrl(rawUrl, msg.id);
  const fileName = msg.file_name || (url ? url.split('/').pop() : 'File');
  const isImage = ft === 'image' || ft === 'gif' || ft === 'photo' || (url && /\.(jpg|jpeg|png|webp|gif|svg|bmp)$/i.test(url.split('?')[0]));
  const isVideo = ft === 'video' || (url && /\.(mp4|mov|webm|3gp)$/i.test(url.split('?')[0]));
  const isAudio = ft === 'audio' || (url && /\.(mp3|ogg|wav|m4a|aac)$/i.test(url.split('?')[0]));

  useEffect(() => {
    setLoadError(false);
  }, [url]);

  if (isImage) {
    if (!url || loadError) {
      return (
        <div className="mb-1 p-2 rounded-lg border border-dashed border-primary/30 max-w-[240px] flex items-center gap-2">
          <Image size={24} className="text-muted-foreground shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium truncate">{fileName}</p>
            <p className="text-[10px] text-muted-foreground">{loadError ? 'Image could not load' : 'Image'}</p>
          </div>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline shrink-0">Open</a>
          )}
        </div>
      );
    }
    return (
      <div className="mb-1">
        <img
          src={url}
          alt={fileName}
          className="max-w-[240px] max-h-[200px] rounded-lg object-cover cursor-pointer hover:opacity-95 transition-opacity"
          onClick={() => window.open(url, '_blank')}
          onError={() => setLoadError(true)}
        />
      </div>
    );
  }
  if (isVideo) {
    return <video src={url} controls className="max-w-[260px] max-h-[200px] rounded-lg mb-1" />;
  }
  if (isAudio) {
    return <audio src={url} controls className="max-w-[240px] mb-1" />;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 border border-white/20 rounded-lg mb-1 hover:bg-white/5 transition-colors" data-testid="file-download">
      <DownloadSimple size={16} />
      <span className="text-xs truncate max-w-[160px]">{fileName}</span>
    </a>
  );
}

function RoleBadge({ role }) {
  if (role === 'super_admin') {
    return <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">Super Admin</span>;
  }
  if (role === 'staff') {
    return <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">Staff</span>;
  }
  return <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">Admin</span>;
}

export default function ChatPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSection = searchParams.get('section') || 'all';

  const [sectionFilter, setSectionFilter] = useState(initialSection);
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [superAdminContacts, setSuperAdminContacts] = useState([]);
  const [staffAdminContacts, setStaffAdminContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const handleBack = useCallback(() => {
    setActiveContact(null);
    hasHistoryPushedRef.current = false;
    if (window.history.state?.adminChatOpen) {
      window.history.back();
    }
  }, []);

  const adjustTextareaHeight = (node) => {
    const el = node || textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 38), 120)}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: line change / newline without sending
        return;
      }
      // Enter without Shift: sends message
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent?.isComposing) {
        e.preventDefault();
        sendMessage(e);
      }
    }
  };

  // Sync sectionFilter when searchParams change (e.g. navigation from sidebar)
  useEffect(() => {
    const s = searchParams.get('section');
    if (s && s !== sectionFilter) {
      setSectionFilter(s);
    } else if (!s && sectionFilter !== 'all') {
      setSectionFilter('all');
    }
  }, [searchParams, sectionFilter]);

  const activeContactRef = useRef(activeContact);
  activeContactRef.current = activeContact;
  const hasHistoryPushedRef = useRef(false);

  // Handle phone / browser hardware back button to return to contact list instead of dashboard
  useEffect(() => {
    const handlePopState = () => {
      if (activeContactRef.current) {
        setActiveContact(null);
        hasHistoryPushedRef.current = false;
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Toggle chat-open class for mobile hamburger hide
  useEffect(() => {
    if (activeContact) document.body.classList.add('chat-open');
    else document.body.classList.remove('chat-open');
    return () => document.body.classList.remove('chat-open');
  }, [activeContact]);

  const fetchContacts = useCallback(() => {
    API.get('/chat/contacts').then(r => {
      setContacts(r.data.contacts || []);
      setSuperAdminContacts(r.data.super_admin_contacts || []);
      setStaffAdminContacts(r.data.staff_admin_contacts || []);
    }).catch(() => {});
  }, []);

  const fetchMessages = useCallback(() => {
    if (!activeContact) return;
    const targetId = activeContact.id;
    API.get(`/chat/messages/${targetId}`).then(r => {
      if (activeContactRef.current?.id !== targetId) return;
      const msgs = r.data.messages || [];
      if (msgs.length > prevMsgCountRef.current && prevMsgCountRef.current > 0) {
        playBeep();
      }
      prevMsgCountRef.current = msgs.length;
      setMessages(prev => {
        const serverIds = new Set(msgs.map(m => m.id));
        const stillOptimistic = prev.filter(m => m._optimistic && m._target_contact_id === targetId && !serverIds.has(m.id));
        return [...msgs, ...stillOptimistic];
      });
    }).catch(() => {});
  }, [activeContact]);

  const fetchTyping = useCallback(() => {
    if (!activeContact) return;
    API.get(`/chat/typing-status/${activeContact.id}`).then(r => setIsTyping(r.data.is_typing)).catch(() => {});
  }, [activeContact]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => {
    if (activeContact) {
      prevMsgCountRef.current = 0;
      fetchMessages();
    }
  }, [activeContact, fetchMessages]);

  // Poll for new messages + typing
  useEffect(() => {
    const interval = setInterval(() => {
      fetchContacts();
      if (activeContact) { fetchMessages(); fetchTyping(); }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeContact, fetchContacts, fetchMessages, fetchTyping]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const totalUnread = contacts.reduce((s, c) => s + (c.unread_count || 0), 0);
  const superUnread = superAdminContacts.reduce((s, c) => s + (c.unread_count || 0), 0);
  const staffUnread = staffAdminContacts.reduce((s, c) => s + (c.unread_count || 0), 0);

  const handleSectionTabClick = (section) => {
    setSectionFilter(section);
    if (section === 'all') {
      searchParams.delete('section');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ section }, { replace: true });
    }
  };

  const filteredSuperContacts = useMemo(() => {
    if (!searchQuery.trim()) return superAdminContacts;
    const q = searchQuery.toLowerCase().trim();
    return superAdminContacts.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.parent_name || '').toLowerCase().includes(q)
    );
  }, [superAdminContacts, searchQuery]);

  const filteredStaffContacts = useMemo(() => {
    if (!searchQuery.trim()) return staffAdminContacts;
    const q = searchQuery.toLowerCase().trim();
    return staffAdminContacts.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.parent_name || '').toLowerCase().includes(q)
    );
  }, [staffAdminContacts, searchQuery]);

  const selectContact = (c) => {
    if (!hasHistoryPushedRef.current) {
      window.history.pushState({ adminChatOpen: true }, '');
      hasHistoryPushedRef.current = true;
    }
    if (activeContact?.id !== c.id) {
      setMessages([]);
      setText('');
      setFile(null);
    }
    setActiveContact(c);
    API.post(`/chat/read/${c.id}`).catch(() => {});
  };

  const sendMessage = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!text?.trim() && !file) return;
    if (!activeContact) return;

    const targetContactId = activeContact.id;
    const outgoingText = text?.trim() || '';
    const outgoingFile = file;

    // Immediately clear input and file to feel instant
    setText('');
    setFile(null);
    if (textareaRef.current) textareaRef.current.style.height = '38px';
    if (fileInputRef.current) fileInputRef.current.value = '';

    const tempId = `opt-${Date.now()}`;
    const fileKind = outgoingFile ? (
      outgoingFile.type?.startsWith('image') ? 'image' :
      outgoingFile.type?.startsWith('video') ? 'video' :
      outgoingFile.type?.startsWith('audio') ? 'audio' : 'file'
    ) : null;

    const optimistic = {
      id: tempId,
      _target_contact_id: targetContactId,
      sender_id: user?.id,
      receiver_id: targetContactId,
      sender_name: user?.name || user?.email || 'You',
      sender_role: user?.role,
      text: outgoingText,
      file_url: outgoingFile ? URL.createObjectURL(outgoingFile) : null,
      file_name: outgoingFile?.name || null,
      file_type: fileKind,
      read: false,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };

    if (activeContactRef.current?.id === targetContactId) {
      setMessages(prev => [...prev, optimistic]);
    }
    setSending(true);

    try {
      const fd = new FormData();
      fd.append('receiver_id', targetContactId);
      if (outgoingText) fd.append('text', outgoingText);
      if (outgoingFile) fd.append('file', outgoingFile);
      const r = await API.post('/chat/send', fd);
      if (activeContactRef.current?.id === targetContactId) {
        if (r.data?.message) {
          setMessages(prev => prev.map(m => m.id === tempId ? r.data.message : m));
        }
      }
      fetchContacts();
    } catch {
      toast.error('Failed to send');
      if (activeContactRef.current?.id === targetContactId) {
        setMessages(prev => prev.filter(m => m.id !== tempId));
      }
    } finally {
      setSending(false);
    }
  };

  const renderContactItem = (c) => (
    <button
      key={c.id}
      data-testid={`chat-contact-${c.id}`}
      onClick={() => selectContact(c)}
      className={`w-full text-left px-3 py-2.5 border-b border-border/40 hover:bg-accent/50 transition-colors flex items-center gap-3 ${activeContact?.id === c.id ? 'bg-accent' : ''}`}
    >
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold shrink-0">
        {(c.name || c.email || '?')[0].toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className="text-sm font-medium truncate">{c.name}</span>
          <div className="flex items-center gap-1.5 shrink-0">
            <RoleBadge role={c.role} />
            {c.unread_count > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">
                {c.unread_count}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <p className="truncate flex-1">
            {c.last_message?.text || (c.last_message?.file_name ? `📎 ${c.last_message.file_name}` : (c.parent_name ? `Staff under ${c.parent_name}` : c.email))}
          </p>
          {c.last_message?.created_at && (
            <span className="text-[10px] ml-1.5 shrink-0 text-muted-foreground/70">
              {c.last_message.created_at.slice(11, 16)}
            </span>
          )}
        </div>
      </div>
    </button>
  );

  const isStaffUser = user?.role === 'staff';

  return (
    <div className="flex flex-1 min-h-0 h-full border border-border rounded-lg overflow-hidden bg-background" data-testid="chat-page">
      {/* Contact list — hidden on mobile when chat open */}
      <div className={`${activeContact ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-88 border-r border-border flex-col bg-background shrink-0`}>
        {/* Header & Filter Controls */}
        <div className="p-3 border-b border-border space-y-2.5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm pl-10 lg:pl-0" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Support Chat
              {totalUnread > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                  {totalUnread}
                </span>
              )}
            </h3>
          </div>

          {/* Section Filter Tabs (Super & Admin vs Staff & Admin) */}
          {!isStaffUser && (
            <div className="flex items-center p-0.5 bg-muted rounded-md text-xs">
              <button
                type="button"
                onClick={() => handleSectionTabClick('all')}
                className={`flex-1 py-1 px-2 rounded font-medium transition-colors text-center ${sectionFilter === 'all' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => handleSectionTabClick('super_admin')}
                className={`flex-1 py-1 px-1.5 rounded font-medium transition-colors flex items-center justify-center gap-1 ${sectionFilter === 'super_admin' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <span>Super & Admin</span>
                {superUnread > 0 && (
                  <span className="px-1 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px]">
                    {superUnread}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => handleSectionTabClick('staff_admin')}
                className={`flex-1 py-1 px-1.5 rounded font-medium transition-colors flex items-center justify-center gap-1 ${sectionFilter === 'staff_admin' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <span>Staff & Admin</span>
                {staffUnread > 0 && (
                  <span className="px-1 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px]">
                    {staffUnread}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Contact Search Input */}
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="h-8 pl-8 text-xs bg-muted/40"
            />
          </div>
        </div>

        {/* Contacts Scrollable View */}
        <div className="flex-1 overflow-y-auto">
          {/* Section: Super Admin & Admin */}
          {(sectionFilter === 'all' || sectionFilter === 'super_admin') && !isStaffUser && (
            <div>
              {sectionFilter === 'all' && (
                <div className="px-3 py-1.5 bg-muted/60 border-b border-border/40 flex items-center justify-between text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <span className="flex items-center gap-1.5">
                    <ChatCircleDots size={14} className="text-primary" /> Super & Admin Chat
                  </span>
                  {superUnread > 0 && (
                    <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full">
                      {superUnread}
                    </span>
                  )}
                </div>
              )}
              {filteredSuperContacts.map(renderContactItem)}
              {filteredSuperContacts.length === 0 && sectionFilter === 'super_admin' && (
                <p className="text-center text-xs text-muted-foreground py-8">No contacts in Super & Admin Chat</p>
              )}
            </div>
          )}

          {/* Section: Staff & Admin */}
          {(sectionFilter === 'all' || sectionFilter === 'staff_admin' || isStaffUser) && (
            <div>
              {(sectionFilter === 'all' || isStaffUser) && (
                <div className="px-3 py-1.5 bg-muted/60 border-b border-border/40 flex items-center justify-between text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <span className="flex items-center gap-1.5">
                    <ChatTeardropText size={14} className="text-amber-500" /> Staff & Admin Chat
                  </span>
                  {staffUnread > 0 && (
                    <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full">
                      {staffUnread}
                    </span>
                  )}
                </div>
              )}
              {filteredStaffContacts.map(renderContactItem)}
              {filteredStaffContacts.length === 0 && (sectionFilter === 'staff_admin' || isStaffUser) && (
                <p className="text-center text-xs text-muted-foreground py-8">No contacts in Staff & Admin Chat</p>
              )}
            </div>
          )}

          {contacts.length === 0 && (
            <p className="text-center text-xs text-muted-foreground py-8">No contacts found</p>
          )}
        </div>
      </div>

      {/* Chat Area — full width on mobile */}
      <div className={`${!activeContact ? 'hidden md:flex' : 'flex'} flex-1 flex-col bg-background min-w-0`}>
        {activeContact ? (
          <>
            {/* Active Contact Header */}
            <div className="px-3 md:px-4 py-3 border-b border-border flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <button
                  type="button"
                  onClick={handleBack}
                  className="md:hidden p-1.5 -ml-1 rounded-full hover:bg-accent text-foreground flex items-center justify-center transition-colors touch-manipulation active:scale-95 shrink-0"
                  data-testid="admin-chat-back-btn"
                  aria-label="Back to contacts"
                  title="Back"
                >
                  <CaretLeft size={22} weight="bold" />
                </button>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold shrink-0">
                  {(activeContact.name || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate">{activeContact.name}</p>
                    <RoleBadge role={activeContact.role} />
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {activeContact.section === 'staff_admin'
                      ? (activeContact.parent_name ? `Staff under ${activeContact.parent_name}` : 'Staff & Admin Chat')
                      : 'Super & Admin Chat'}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map(msg => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-xl px-3 py-2 ${isMine ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                      {msg.file_url && <MediaBubble msg={msg} />}
                      {msg.text && <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>}
                      <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'justify-end' : ''}`}>
                        <span className={`text-[10px] ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                          {msg.created_at?.replace('T', ' ').slice(11, 16)}
                        </span>
                        {isMine && (
                          <span className={`text-[10px] ${msg.read ? 'text-blue-400' : isMine ? 'text-primary-foreground/40' : 'text-muted-foreground/40'}`}>
                            {msg.read ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-xl rounded-bl-sm px-3 py-2">
                    <div className="flex gap-1 items-center">
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              {messages.length === 0 && <p className="text-center text-sm text-muted-foreground py-12">No messages yet. Start the conversation!</p>}
            </div>

            <FilePreview file={file} onRemove={() => setFile(null)} />

            {/* Chat Input Bar */}
            <form onSubmit={sendMessage} className="p-2 sm:p-2.5 border-t border-border flex items-end gap-1.5 sm:gap-2 bg-background pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]">
              <input ref={fileInputRef} type="file" className="hidden" accept="image/*,video/*,audio/*,.gif,.pdf,.doc,.docx,.txt" onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); e.target.value = ''; }} />
              <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} className="shrink-0 mb-0.5 rounded-full w-9 h-9 p-0 hover:bg-muted text-muted-foreground hover:text-foreground touch-manipulation" data-testid="chat-attach-btn">
                <Paperclip size={20} />
              </Button>
              <textarea
                ref={textareaRef}
                data-testid="chat-text-input"
                value={text}
                rows={1}
                onChange={e => {
                  setText(e.target.value);
                  adjustTextareaHeight(e.target);
                  if (activeContact) {
                    clearTimeout(typingTimeoutRef.current);
                    API.post(`/chat/typing/${activeContact.id}`).catch(() => {});
                    typingTimeoutRef.current = setTimeout(() => {}, 3000);
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                className="flex-1 resize-none bg-muted/40 hover:bg-muted/60 focus:bg-background border border-border/80 focus:border-primary rounded-2xl px-3.5 py-2 text-sm focus:outline-none transition-colors min-h-[38px] max-h-[120px] overflow-y-auto leading-relaxed msg-input"
                style={{ height: '38px' }}
              />
              <Button type="submit" size="sm" disabled={sending || (!text?.trim() && !file)} className="shrink-0 rounded-full w-9 h-9 p-0 mb-0.5 shadow-sm active:scale-95 transition-transform touch-manipulation bg-primary text-primary-foreground flex items-center justify-center" data-testid="chat-send-btn">
                <PaperPlaneTilt size={18} weight="fill" />
              </Button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <p className="text-4xl mb-3">💬</p>
              <p className="text-sm font-medium">Select a contact to start chatting</p>
              <p className="text-xs text-muted-foreground mt-1">Super & Admin chats and Staff & Admin chats are organized in the left panel</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
